<template>
    <div>
        <div class="text-right" v-if="licenseKey && licenseData">
            <a class="btn btn-outline-primary" v-on:click="logout" href="javascript:;">Abmelden</a>
        </div>
        <login v-if="!licenseKey" v-on:login="login" :lic-tool-root="licToolRoot"/>

        <div v-else-if="licenseData && 'pending' in licenseData">
            <div class="alert alert-info">
              <div>
                <span class="fa fa-spin fa-spinner"></span>
                Lizenzdaten werden geladen
              </div>
            </div>
        </div>
        <div v-else-if="licenseData && 'error' in licenseData">
            <div class="alert alert-danger">
              <div>
                {{ licenseData.error }}
              </div>
            </div>
        </div>
        <div v-else-if="licenseData">
            <div class="row">
                <div class="col-lg-6">
                    <h2>Ihr Softwareprodukt: {{licenseData.product}}</h2>
                    <div class="card card-body mb-4">
                        <p>{{licenseData.restriction}}</p>
                        <p>Ihr zuständiger Berater bei der GRB: <span class="highlight">{{licenseData.consultant}}</span></p>
                    </div>
                    <div v-if="!('lastDownload' in licenseData) || !licenseData.lastDownload.version" class="alert alert-info">
                      <div>
                        Sie haben die Software bisher nicht über diese Lizenzverwaltung herunter geladen. Laden Sie die Software immer hier herunter, um Ihre aktuelle Version hier einsehen zu können.
                      </div>
                    </div>
                    <p v-else>
                        Sie haben zuletzt die <font class="customer-data">Version {{licenseData.lastDownload.version}}</font> herunter geladen.
                    </p>
                    <p v-if="getUrlVars()['myVersion']"> <!-- we only show this information when called via RISCage -->
                        Ihre derzeit installierte Version ist die {{getUrlVars()['myVersion']}}
                    </p>
                    <div v-if="productVersions.length > 0 && (productVersions[0].name !== getUrlVars()['myVersion'] ? getUrlVars()['myVersion'] : licenseData.lastDownload.version)" class="alert alert-info">
                       <div>
                         Bitte aktualisieren Sie auf die aktuelle Version!
                       </div>
                    </div>

                    <div v-if="licenseData.download">
                        <p v-html="licenseData.download.notes"></p>
                        <p class="text-right">
                            <a :href="licToolRoot + '/' + licenseData.download.link" class="btn btn-outline-primary"> Download Version {{ licenseData.download.version }}</a>
                        </p>
                    </div>
                    <div class="text-center" v-if="!productVersions.length">
                        <span class="fa fa-3x fa-spin fa-spinner"/>
                    </div>
                    <div v-for="version in productVersions" :key="version.name" class="productversion-wrapper">
                        <h3 class="d-flex d-space-between"><span>{{version.name}}</span> <small class="ml-auto">{{ moment(version.date).format('LL') }}</small></h3>
                        <ul>
                            <li v-for="(change, i) in version.changelog" :key="i">
                                {{change}}
                            </li>
                        </ul>
                    </div>
                </div>
                <div class="col-lg-6">
                    <h2> Hinterlegte Kontaktdaten </h2>
                    <p> Bitte halten Sie die hier hinterlegten Daten immer auf dem aktuellen Stand, damit unser Support Sie bei Problemen schnell kontaktieren kann.</p>
                    <p> Insbesondere werden diese Daten benötigt, um Ihnen Ihre verlängerten Lizenzen zukommen zu lassen.</p>

                    <div><strong> Fachlicher Kontakt </strong></div>
                    <contact :contact="licenseData.contact"/>
                    <br>
                    <div class="mt-1"><strong> Kontakt der IT-Abteilung </strong></div>
                    <contact :contact="licenseData.itContact"/>

                    <p class="text-right" style="margin-top: 15px;">
                        <a class="btn btn-outline-primary" v-on:click="editContact" href="javascript:;">Daten ändern</a>
                    </p>

                    <h2> Ausgestellte Lizenzen </h2>
                    <div class="alert alert-info mt-2" v-if="licenseData.renewable">
                      <div>
                        <p> Ihre Lizenz kann verlängert werden. </p>
                        <p class="text-right">
                          <a class="btn btn-outline-primary" v-on:click="renew" href="javascript:;"> Lizenz verlängern </a>
                        </p>
                      </div>
                    </div>
                    <div class="list-group">
                        <div v-for="(item, i) in licenseData.licenses" :key="i" class="list-group-item" :class="['license-state-' + item.state]">
                            {{ moment(item.created).format('LL') }}
                            bis
                            {{ moment(item.valid_until).format('LL') }}

                            <div v-if="'max_org_unit' in item && item.max_org_unit > 0">
                                {{ item.max_org_unit }} Standorte
                            </div>

                            <div class="text-right" v-if="item.state == 'active' || item.state == 'renewable'">
                                <a :href="generateDownloadLink(item)" class="btn btn-outline-primary" v-b-modal.licenseHelp>Lizenzdatei herunterladen</a>
                            </div>

                        </div>
                    </div>

                </div>
            </div>

            <edit-contacts :license-key="licenseKey" :license-data="licenseData" :lic-tool-root="licToolRoot" ref="editContact" @updated="updateCount = updateCount + 1"/>

            <b-modal id="licenseHelp" title="Lizenzdownload gestartet" ok-only ok-title="Anleitung schließen" size="lg">
                <p>Der Lizenzdownload wurde gestartet. Bitte speichern sie die Datei lokal</p>

                <license-upload-riscage v-if="licenseData.product == 'riscage'"/>
                <license-upload-riskop v-if="licenseData.product == 'riskop'"/>
            </b-modal>
        </div>

        <div v-else class="alert alert-danger">
          <div>
            Die angegebene Lizenz Id ist nicht valide.
          </div>
        </div>
    </div>
</template>

<script>
  import Login from "./Login.vue";
  import Contact from "./Contact.vue";
  import EditContacts from "./EditContacts.vue";
  import LicenseUploadRiscage from "./help/LicenseUploadRiscage.vue";
  import LicenseUploadRiskop from "./help/LicenseUploadRiskop.vue";
  import moment from "moment";

  export default {
    props: {
      releaseApiUrl: String,
      licenseKeyDefault: {
        type: String,
        default: ''
      },
      licToolRoot: {
        type: String,
        required: true
      },
    },
    data() {
      return {
        licenseKey: this.getUrlVars()['UUID'] ? this.getUrlVars()['UUID'] : this.licenseKeyDefault,
        moment,
        updateCount: 0,
      };
    },
    methods: {
      getUrlVars() {
        var vars = {};
        var parts = window.location.href.replace(/[?&]+([^=&]+)=([^&]*)/gi, function(m,key,value) {
          vars[key] = value;
        });
        return vars;
      },

      login(sessionData) {
        this.licenseKey = sessionData["licenseKey"];
      },
      renew() {
        this.$refs.editContact.showForRenew();
      },
      editContact() {
        this.$refs.editContact.showForEdit();
      },
      logout() {
        this.licenseKey = null;
      },
      generateDownloadLink(item) {
        return this.licToolRoot + '/customer/' + this.licenseKey + '/download/' + this.licenseData.name  + '-' + moment(item.valid_until).format('YYYY-MM-DD') + ".lic";
      }
    },
    asyncComputed: {
      licenseData: {
        get() {
          if (!this.licenseKey) {
            return new Promise(resolve =>
              resolve({
                pending: true
              })
            );
          }

          // if the parameters are given (call from RISCage) we send the data to the license manager
          // so we can store this information with the customer license
          let formData = new FormData();
          if (this.getUrlVars()['myVersion']) {
            formData.append("clientData",
              JSON.stringify({
                package: this.getUrlVars()['package'],
                myCustomization: this.getUrlVars()['myCustomization'],
                myVersion: this.getUrlVars()['myVersion'],
                myBuild: this.getUrlVars()['myBuild']
              })
            );
          }
          return this.$http
            .post(this.licToolRoot + "/customer/" + this.licenseKey + "/licenses.json", formData)
            .then(response => response.body.data)
            .catch(error => {
              return {
                error: "Fehler beim Abruf der Lizenzdaten."
              };
            });
        },
        watch() {
          // this triggers a refresh every time updateCount changes.
          this.updateCount;
        },
        default: {}
      },
      productVersions: {
        get() {
          if (!this.licenseData || !this.licenseData.product) {
            return new Promise(resolve => resolve([]));
          }
          let params = {
            product: this.licenseData.product
          };

          return this.$http
            .post(this.releaseApiUrl, params, {
              emulateJSON: true
            })
            .then(response => response.body.data)
            .catch(error => [])
        },
        default: []
      }
    },
    components: {
      Login,
      Contact,
      EditContacts,
      LicenseUploadRiscage,
      LicenseUploadRiskop
    }
  };
</script>
